import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import { useAppDispatch, useAppSelector } from 'shared/lib/hooks/hooks';
import { getMarathons } from 'api/thunk/marathons/getActions/getMarathons';
import { useNavigate } from 'react-router-dom';
import { CustomPagination } from 'components/CustomPagination/CustomPagination';
import { setMarathonsPage, setMarathonsPageSize } from 'api/slice/marathonsSlice/marathonsSlice';

const BASE_URL = 'https://marathon-app.timelysoft.org/';

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const MyPhotosMarathonsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: marathons, loading } = useAppSelector((state) => state.marathons.marathons.response ?? []);
  const { page, pageSize } = useAppSelector((state) => state.marathons.marathons.params);

  useEffect(() => {
    dispatch(getMarathons({ page: page, pageSize: pageSize }));
  }, [dispatch, page, pageSize]);

  const handlePageChange = (newPage: number) => {
    dispatch(setMarathonsPage(newPage));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    dispatch(setMarathonsPageSize(newPageSize));
    dispatch(setMarathonsPage(1));
  };


  const handleCardClick = (marathonId: number) => {
    navigate(`/personal_cabinet/my-photos/${marathonId}`);
  };

  return (
    <Paper sx={{ borderRadius: '16px', padding: '24px', boxShadow: 'none' }}>
      <Typography variant="h5" sx={{ fontWeight: "600" }} component="h2" mb={3}>
        Выберите марафон
      </Typography>

      <Grid container spacing={3}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))
          : marathons?.query?.map((marathon) => (
            <Grid item xs={12} sm={6} md={4} key={marathon.id}>
              <Card
                sx={{
                  borderRadius: '12px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(marathon.id)}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={marathon.logo ? `${BASE_URL}${marathon.logo}` : '/placeholder-marathon.jpg'}
                    alt={marathon.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontSize="1rem" fontWeight={600} lineHeight={1.3}>
                        {marathon.name}
                      </Typography>
                      <Chip
                        label={marathon.isActive ? 'Активен' : 'Завершён'}
                        size="small"
                        color={marathon.isActive ? 'success' : 'default'}
                        sx={{ ml: 1, flexShrink: 0 }}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5} mt={1.5} color="text.secondary">
                      <CalendarTodayIcon fontSize="small" />
                      <Typography variant="body2">{formatDate(marathon.date)}</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5} color="text.secondary">
                      <LocationOnIcon fontSize="small" />
                      <Typography variant="body2" noWrap title={marathon.place}>
                        {marathon.place}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5} color="text.secondary">
                      <PeopleIcon fontSize="small" />
                      <Typography variant="body2">
                        {marathon.participants} участников
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
      </Grid>

      <CustomPagination
        totalCount={marathons?.count ?? 0}
        page={page ?? 1}
        pageSize={pageSize ?? 10}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </Paper>
  );
};

export default MyPhotosMarathonsPage;